/** @jsx jsx */
import { useCallback, useEffect, useState } from 'react';
import { css, jsx } from '@compiled/react';

type Props = {
	fromCenter: boolean;
	fromElementId: string;
	toElementId: string;
	strokeWidth?: number;
	color: string;
};

const FROM_X_OFFSET = 55;
const FROM_X_CENTER_OFFSET = 130;
const FROM_Y_OFFSET = 5;
const CONTROL_X_OFFSET = 10;
const CONTROL_X_CENTER_OFFSET = -12;
const CONTROL_Y_OFFSET = 20;
const TO_Y_TOP_OFFSET = 10;
const TO_Y_BOTTOM_OFFSET = 10;
const ARROW_ANGLE = Math.PI / 4;
const ARROW_LENGTH = 8;

export const Arrow = ({
	fromCenter,
	fromElementId,
	toElementId,
	strokeWidth = 2,
	color = 'white',
}: Props) => {
	const [svgPosition, setSvgPosition] = useState({
		left: 0,
		top: 0,
		width: 0,
		height: 0,
		path: '',
	});

	const calculatePath = useCallback(() => {
		const fromElement = globalThis.document.getElementById(fromElementId);
		const toElement = globalThis.document.getElementById(toElementId);
		const parentElement = fromElement?.offsetParent;

		if (!fromElement || !toElement || !parentElement) {
			return;
		}

		const fromRect = fromElement.getBoundingClientRect();
		const toRect = toElement.getBoundingClientRect();
		const parentRect = parentElement.getBoundingClientRect();

		// Calculate the positions relative to the parent container
		const fromX =
			(fromCenter ? toRect.left - FROM_X_CENTER_OFFSET : toRect.left - FROM_X_OFFSET) -
			parentRect.left;
		const fromY = fromRect.top + (fromRect.height / 2 + FROM_Y_OFFSET) - parentRect.top;
		const toX = fromX + FROM_X_OFFSET + 5;

		const toY =
			Math.max(toRect.bottom - TO_Y_BOTTOM_OFFSET, fromRect.top + TO_Y_TOP_OFFSET) - parentRect.top;

		const midX = (fromX + toX) / 2;
		const midY = (fromY + toY) / 2;

		// Offset the midpoint vertically to create an elliptical arc
		const controlX = fromCenter ? midX + CONTROL_X_CENTER_OFFSET : midX + CONTROL_X_OFFSET;
		const controlY = midY + CONTROL_Y_OFFSET;

		const dx = toX - controlX;
		const dy = toY - controlY;
		const angle = Math.atan2(dy, dx);

		const arrowPoint1X = toX - ARROW_LENGTH * Math.cos(angle - ARROW_ANGLE);
		const arrowPoint1Y = toY - ARROW_LENGTH * Math.sin(angle - ARROW_ANGLE);
		const arrowPoint2X = toX - ARROW_LENGTH * Math.cos(angle + ARROW_ANGLE);
		const arrowPoint2Y = toY - ARROW_LENGTH * Math.sin(angle + ARROW_ANGLE);

		const path = `
			M ${fromX} ${fromY}
			Q ${controlX} ${controlY}, ${toX} ${toY}
			M ${toX} ${toY}
			L ${arrowPoint1X} ${arrowPoint1Y}
			M ${toX} ${toY}
			L ${arrowPoint2X} ${arrowPoint2Y}
		`;

		const minX = Math.min(fromX, toX) - ARROW_LENGTH;
		const minY = Math.min(fromY, toY) - ARROW_LENGTH;
		const maxX = Math.max(fromX, toX) + ARROW_LENGTH;
		const maxY = Math.max(fromY, toY) + ARROW_LENGTH;

		setSvgPosition({
			left: minX,
			top: Math.min(minY, fromY),
			width: maxX - minX,
			height: maxY - minY,
			path,
		});
	}, [fromElementId, toElementId, fromCenter]);

	useEffect(() => {
		const updateArrow = () => {
			requestAnimationFrame(calculatePath);
		};

		updateArrow();

		globalThis.window.addEventListener('resize', updateArrow);

		return () => {
			globalThis.window.removeEventListener('resize', updateArrow);
		};
	}, [fromElementId, toElementId, calculatePath]);

	return (
		<svg
			css={styles}
			width={svgPosition.width}
			height={svgPosition.height}
			viewBox={`0 0 ${svgPosition.width} ${svgPosition.height}`}
			// eslint-disable-next-line jira/react/no-style-attribute
			style={{
				left: svgPosition.left,
				top: svgPosition.top,
			}}
		>
			<path
				d={svgPosition.path}
				stroke={color}
				strokeWidth={strokeWidth}
				fill="none"
				transform={`translate(${-svgPosition.left}, ${-svgPosition.top})`}
			/>
		</svg>
	);
};

const styles = css({
	zIndex: 1,
	position: 'absolute',
	pointerEvents: 'none',
});
